import React, { useEffect, useState } from 'react'

import PropTypes from 'prop-types'

import {
  CheckRounded as CheckRoundedIcon,
  CloseRounded as CloseRoundedIcon,
  Edit as EditIcon,
  TimerRounded as TimerRoundedIcon,
} from '@mui/icons-material'
import {
  Box,
  FormControlLabel,
  Icon,
  IconButton,
  ListItem,
  ListItemSecondaryAction,
  Radio,
} from '@mui/material'
import { makeStyles } from '@mui/styles'

import { IMask } from 'react-imask'

import { Can } from '~/src/Flags'
import createLogger from '~/src/Lib/Logging'
import { idPropType } from '~/src/Lib/PropTypes'
import {
  durationToMillis,
  EMPTY_OBJECT,
  noop,
  parseDuration,
  shortDurationHumanizer,
} from '~/src/Lib/Utils'
import MaskedTextField from '~/src/UI/Shared/Form/MaskedTextField'
import T from '~/src/UI/Shared/Typography'
import PersonWidget from '~/src/UI/Widgets/Person'

const displayName = 'Task/AssigneeItem'
const logger = createLogger(displayName)

const useStyles = makeStyles(theme => ({
  assigneeListItem: {
    minHeight: 48,
    maxHeight: 48,
    padding: `${theme.spacing(1, 0, 1, 1.5)} !important`,
    '& > div > p': {
      color: theme.palette.contrast.strong
    },
    '& [class*=MuiFormControlLabel-root]': {
      marginLeft: 0,
      minWidth: 32,
      justifyContent: 'center'
    },
  },
  radioLabel: {
    '& [class*=MuiButtonBase-root]': {
      color: theme.palette.primary.main,
      padding: 0
    },
    '& [class*=MuiTypography-body1]': {
      color: theme.palette.contrast.main,
      fontSize: 10
    },
  },
  assigneeWidget: {
    columnGap: theme.spacing(3)
  },
  secondaryAction: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  discardButton: {
    '& [class*=MuiSvgIcon-root]': {
      color: theme.palette.primary.main,
    },
  },
  timerIcon: {
    color: theme.palette.error.main,
    paddingRight: theme.spacing(3.2)
  },
  checkIcon: {
    color: theme.palette.batch.main,
    paddingRight: theme.spacing(3.2)
  },
  timeSpentField: {
    minHeight: '2.5rem',
  },
  inputHidden: {
    display: 'none'
  },
  timeSpentText: {
    width: theme.spacing(20),
    fontSize: theme.spacing(1.5),
    color: theme.palette.text.hint,
  }
}))

/**
 * Formats the time spent input value.
 * @param {string} timeSpent unformatted time spent string
 * @returns formatted time spent string
 */
const formatHour = timeSpent => {
  if (timeSpent.length < 5) {
    const formattedTime = timeSpent.padEnd(4, '0')
    return `${formattedTime.slice(0, 2)}:${formattedTime.slice(2)}:00`
  }
  return `${timeSpent[0]} ${timeSpent.slice(1, 3)}:${timeSpent.slice(3)}:00`
}

/**
 * Formatts the assigne spent time to use it in a masked field
 * @param {string} timeSpentStr The original assignee spent time formatted
 * @returns The assignee spent time without symbols, such as ```: . \ <<bank space>>```
 * */
const formatToValue = timeSpentStr => {
  let parts = timeSpentStr.split('.')
  parts = parts[0].split(/\D/g)
  if (parts.length > 3) {
    return parts.slice(0, 3).join('')
  }
  return `0${parts.slice(0, 2).join('')}`
}

const timeSpentMaskOptions = {
  mask: '0 d HH h MM m',
  blocks: {
    HH: {
      mask: IMask.MaskedRange,
      from: 0,
      to: 23
    },
    MM: {
      mask: IMask.MaskedRange,
      from: 0,
      to: 59
    }
  },
  lazy: false,
  overwrite: true,
  placeholderChar: '0'
}

const timeSpentPipe = IMask.createPipe(timeSpentMaskOptions)

const AssigneeItem = ({
  assignee = {},
  dialogRouteInfo: { url = null } = EMPTY_OBJECT,
  disabled = false,
  hasPermission = false,
  index,
  iAmLead,
  isReviewingSchedule = false,
  lead = null,
  removeAssignee,
  setLead = noop,
  setSpentTime = noop,
}) => {
  const [isDisabled, setIsDisabled] = useState(false)
  const [formattedValue, setFormattedValue] = useState('')
  const [timeWorked, setTimeWorked] = useState(() => {
    if (!assignee.timeSpent) return '00000'
    const { timeSpent } = assignee
    return timeSpent.slice(0, timeSpent.lastIndexOf(':')).replace(/\D/g, '').padStart(5, '0')
  })
  const isLead = (lead === assignee.membership)
  const durationParsed = parseDuration(assignee?.timeSpent)
  const timeSpent = time => shortDurationHumanizer(durationToMillis(time))
  const classes = useStyles({ isDisabled })
  useEffect(() => {
    setIsDisabled(Boolean(durationParsed))
    if (durationParsed) {
      const numValue = formatToValue(assignee?.timeSpent)
      setTimeWorked(numValue)
      const formatted = timeSpentPipe(numValue)
      setFormattedValue(formatted)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  logger.debug('[lead]', { assignee, isLead, lead })

  const onChangeDisabled = () => setIsDisabled(oldValue => !oldValue)
  const notDuplicate = !(url?.includes('duplicate'))
  const assigneeId = `task-assignee-${index}`
  // console.log('assignee', assignee)
  return (
    <ListItem key={assignee.membership} className={classes.assigneeListItem} ContainerProps={{ 'aria-labelledby': assigneeId }}>
      {isReviewingSchedule ? null : (
        <FormControlLabel
          name="assigned"
          checked={isLead}
          className={classes.radioLabel}
          control={<Radio />}
          data-testid={`assignee-${index}`}
          disabled={disabled}
          label={isLead ? 'LEAD' : null}
          labelPlacement="bottom"
          onChange={hasPermission ? () => setLead(assignee.membership) : false}
        />
      )}
      <PersonWidget
        id={assigneeId}
        membership={assignee.membership}
        classes={{ root: classes.assigneeWidget }}
        titleProps={{ variant: 'body1' }}
      />
      <ListItemSecondaryAction classes={{ root: classes.secondaryAction }}>
        {assignee?.lastStartTs ? (
          <Icon className={classes.timerIcon}>
            <TimerRoundedIcon />
          </Icon>
        ) : null}
        {isReviewingSchedule ? (
          <Box display="flex" alignItems="center" columnGap="0.5rem">
            {isDisabled ? <div className={classes.timeSpentText}>{formattedValue}</div> : null}
            <MaskedTextField
              assignee={assignee}
              classes={classes}
              value={timeWorked}
              disabled={isDisabled}
              onChange={(value, mask) => {
                const unmaskedValue = mask.value.replace(/\D/g, '')
                setTimeWorked(value.padEnd(5, '0'))
                setSpentTime(formatHour(unmaskedValue), index)
              }}
            />
            {isDisabled ? (
              <IconButton
                size="small"
                className={classes.discardButton}
                data-testid={`edit-assignee-${assignee.membership}`}
                disabled={disabled}
                onClick={onChangeDisabled}
              >
                <EditIcon />
              </IconButton>
            ) : <Box mr={3.75} />}
          </Box>
        ) : (
          <>
            <Can any actions={['change_task', iAmLead]}>
              {notDuplicate && Boolean(assignee?.lastStartTs || durationParsed) ? [] : (
                <IconButton
                  size="small"
                  className={classes.discardButton}
                  aria-label={`Delete assignee ${index}`}
                  data-testid="delete-assignee"
                  disabled={disabled}
                  onClick={() => removeAssignee(assignee)}
                >
                  <CloseRoundedIcon />
                </IconButton>
              )}
            </Can>
            {notDuplicate && durationParsed ? (
              <T>{timeSpent(assignee?.timeSpent)}</T>
            ) : null}
            {notDuplicate && Boolean(durationParsed && !assignee?.lastStartTs) ? (
              <Icon className={classes.checkIcon}>
                <CheckRoundedIcon />
              </Icon>
            ) : null}
          </>
        )}
      </ListItemSecondaryAction>
    </ListItem>
  )
}

AssigneeItem.displayName = displayName

AssigneeItem.propTypes = {
  arrayHelpers: PropTypes.shape({
    remove: PropTypes.func,
  }).isRequired,
  assignee: PropTypes.shape({
    id: idPropType,
    lastStartTs: PropTypes.string,
    membership: idPropType,
    note: idPropType,
    timeSpent: PropTypes.string
  }),
  dialogRouteInfo: PropTypes.shape({
    url: PropTypes.string
  }),
  disabled: PropTypes.bool,
  hasPermission: PropTypes.bool,
  iAmLead: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  isReviewingSchedule: PropTypes.bool,
  lead: idPropType.isRequired,
  setLead: PropTypes.func,
  setSpentTime: PropTypes.func,
  removeAssignee: PropTypes.func.isRequired,
}

export default AssigneeItem
